import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertTriangle, Search, Download, Package, TrendingDown, TrendingUp, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { stockReorderAPI } from '../../services/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const SEVERITY = {
  critical: { label: 'Critical', color: '#dc2626', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: XCircle },
  warning: { label: 'Warning', color: '#d97706', bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  overstocked: { label: 'Overstocked', color: '#7c3aed', bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', badge: 'bg-violet-100 text-violet-700', icon: TrendingUp },
  ok: { label: 'OK', color: '#059669', bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const fmtINR = (v) => {
  if (!v || v === 0) return '-';
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)} K`;
  return v.toLocaleString('en-IN');
};

const fmtFull = (v) => (!v || v === 0) ? '-' : v.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const StockReorderAlerts = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sevFilter, setSevFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');

  useEffect(() => { loadAlerts(); }, []);

  const loadAlerts = async () => {
    setLoading(true);
    try { setData(await stockReorderAPI.getAlerts()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const categories = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.categories || {}).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = [...data.alerts];
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        r.product_name.toLowerCase().includes(q) ||
        (r.sku || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q)
      );
    }
    if (sevFilter !== 'all') rows = rows.filter(r => r.severity === sevFilter);
    if (catFilter !== 'all') rows = rows.filter(r => r.category === catFilter);
    return rows;
  }, [data, search, sevFilter, catFilter]);

  const exportToExcel = () => {
    if (!data) return;
    const rows = filtered.map(a => ({
      'Severity': SEVERITY[a.severity]?.label || a.severity,
      'Product': a.product_name,
      'SKU': a.sku || '',
      'Category': a.category,
      'Unit': a.unit,
      'Current Qty': a.current_qty,
      'Min Stock': a.min_stock,
      'Max Stock': a.max_stock,
      'Stock %': `${a.stock_pct}%`,
      'Reorder Qty': a.reorder_qty,
      'Cost Price': a.cost_price,
      'Reorder Value': a.reorder_value,
      'Current Value': a.current_value,
    }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = Array(13).fill({ wch: 16 });
    XLSX.utils.book_append_sheet(wb, ws, 'Reorder Alerts');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Stock_Reorder_Alerts_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader2 className="w-6 h-6 animate-spin text-blue-600" /><span className="ml-2 text-sm text-gray-500">Analyzing stock levels...</span>
    </div>
  );
  if (!data) return <p className="text-sm text-gray-500 text-center py-16">No data</p>;

  const { summary: s } = data;

  return (
    <div data-testid="stock-reorder-alerts" className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#24549c15' }}>
            <Package className="w-4 h-4" style={{ color: '#24549c' }} />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }} data-testid="reorder-total-products">{s.with_thresholds}</p>
            <p className="text-[10px] text-gray-500">Tracked Products</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-red-50">
            <XCircle className="w-4 h-4 text-red-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-red-600" style={{ fontFamily: 'DM Sans' }} data-testid="reorder-critical-count">{s.critical}</p>
            <p className="text-[10px] text-gray-500">Critical (Below Min)</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-50">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-amber-600" style={{ fontFamily: 'DM Sans' }} data-testid="reorder-warning-count">{s.warning}</p>
            <p className="text-[10px] text-gray-500">Warning (Near Min)</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-emerald-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-50">
            <CheckCircle className="w-4 h-4 text-emerald-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-emerald-600" style={{ fontFamily: 'DM Sans' }} data-testid="reorder-ok-count">{s.ok}</p>
            <p className="text-[10px] text-gray-500">OK</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-violet-200 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-50">
            <TrendingUp className="w-4 h-4 text-violet-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-violet-600" style={{ fontFamily: 'DM Sans' }} data-testid="reorder-overstocked-count">{s.overstocked}</p>
            <p className="text-[10px] text-gray-500">Overstocked</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gray-50">
            <TrendingDown className="w-4 h-4 text-gray-500" />
          </div>
          <div>
            <p className="text-lg font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }} data-testid="reorder-value">{fmtINR(s.total_reorder_value)}</p>
            <p className="text-[10px] text-gray-500">Est. Reorder Value</p>
          </div>
        </div>
      </div>

      {/* Critical Alert Banner */}
      {s.critical > 0 && (
        <div className="p-3 rounded-xl border border-red-200 bg-red-50/50 flex items-start gap-3" data-testid="critical-alert-banner">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-red-800">
              {s.critical} product{s.critical > 1 ? 's' : ''} below minimum stock level
              {s.no_stock > 0 && <span className="ml-1">({s.no_stock} with zero stock)</span>}
            </p>
            <p className="text-[10px] text-red-600 mt-0.5">
              Estimated reorder cost: INR {fmtFull(s.critical_value)} — immediate procurement action recommended
            </p>
          </div>
        </div>
      )}

      {/* Category Breakdown */}
      {categories.length > 1 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="text-xs font-bold text-gray-900 mb-3">By Category</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(data.categories).sort((a, b) => (b[1].critical + b[1].warning) - (a[1].critical + a[1].warning)).map(([cat, d]) => (
              <div key={cat} className={`p-2.5 rounded-lg border cursor-pointer transition-colors ${
                catFilter === cat ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-100 hover:bg-gray-100'
              }`} onClick={() => setCatFilter(catFilter === cat ? 'all' : cat)}>
                <p className={`text-[10px] font-bold truncate ${catFilter === cat ? 'text-blue-700' : 'text-gray-700'}`}>{cat}</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {d.critical > 0 && <span className="text-[9px] font-bold text-red-600">{d.critical} crit</span>}
                  {d.warning > 0 && <span className="text-[9px] font-bold text-amber-600">{d.warning} warn</span>}
                  {d.ok > 0 && <span className="text-[9px] text-emerald-600">{d.ok} ok</span>}
                  {d.overstocked > 0 && <span className="text-[9px] text-violet-600">{d.overstocked} over</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="reorder-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search product, SKU, category..."
            className="w-full pl-9 pr-4 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-200" />
        </div>
        <div className="flex gap-1.5">
          {[
            { id: 'all', label: 'All', color: null },
            { id: 'critical', label: 'Critical', color: '#dc2626' },
            { id: 'warning', label: 'Warning', color: '#d97706' },
            { id: 'overstocked', label: 'Overstocked', color: '#7c3aed' },
            { id: 'ok', label: 'OK', color: '#059669' },
          ].map(f => (
            <button key={f.id} onClick={() => setSevFilter(f.id)} data-testid={`reorder-filter-${f.id}`}
              className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${
                sevFilter === f.id ? 'text-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              style={sevFilter === f.id
                ? { backgroundColor: f.color || '#24549c' }
                : f.color ? { color: f.color } : { color: '#4b5563' }
              }>
              {f.label} ({f.id === 'all' ? data.alerts.length : data.alerts.filter(a => a.severity === f.id).length})
            </button>
          ))}
        </div>
        <button onClick={exportToExcel} data-testid="export-reorder-excel"
          className="flex items-center gap-1.5 px-4 py-2 text-[11px] font-semibold text-white rounded-lg hover:opacity-90"
          style={{ backgroundColor: '#0d6b3e' }}>
          <Download className="w-3.5 h-3.5" />Excel
        </button>
      </div>

      {/* Alerts Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="reorder-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase w-20">Status</th>
                <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Product</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Current</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Min</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Max</th>
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Level</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Reorder Qty</th>
                <th className="text-right px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Est. Cost</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-8 text-sm text-gray-400">No items match filters</td></tr>
              ) : filtered.map((a, idx) => {
                const sev = SEVERITY[a.severity] || SEVERITY.ok;
                const SevIcon = sev.icon;
                // Stock level bar
                const barPct = Math.min(a.stock_pct, 200);
                const minPct = a.max_stock > 0 ? (a.min_stock / a.max_stock) * 100 : 50;
                return (
                  <tr key={idx} data-testid={`reorder-row-${idx}`}
                    className={`border-b border-gray-50 hover:bg-gray-50/50 ${a.severity === 'critical' ? 'bg-red-50/30' : a.severity === 'warning' ? 'bg-amber-50/20' : ''}`}>
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full ${sev.badge}`}>
                        <SevIcon className="w-3 h-3" />{sev.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-xs font-medium text-gray-900">{a.product_name}</p>
                      <p className="text-[10px] text-gray-400">{a.category}{a.sku ? ` | ${a.sku}` : ''}</p>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <span className={`text-xs font-bold ${a.current_qty <= 0 ? 'text-red-600' : a.severity === 'critical' ? 'text-red-600' : 'text-gray-900'}`}>
                        {fmtFull(a.current_qty)}
                      </span>
                      <span className="text-[9px] text-gray-400 ml-0.5">{a.unit}</span>
                    </td>
                    <td className="px-3 py-3 text-right text-xs text-gray-600">{fmtFull(a.min_stock)}</td>
                    <td className="px-3 py-3 text-right text-xs text-gray-600">{a.max_stock > 0 ? fmtFull(a.max_stock) : '-'}</td>
                    <td className="px-3 py-3">
                      <div className="w-20 mx-auto">
                        <div className="h-2 bg-gray-100 rounded-full overflow-hidden relative">
                          {a.max_stock > 0 && (
                            <div className="absolute h-full w-px bg-gray-400" style={{ left: `${minPct}%` }} title={`Min: ${a.min_stock}`} />
                          )}
                          <div className={`h-full rounded-full transition-all ${
                            a.severity === 'critical' ? 'bg-red-500' :
                            a.severity === 'warning' ? 'bg-amber-500' :
                            a.severity === 'overstocked' ? 'bg-violet-500' : 'bg-emerald-500'
                          }`} style={{ width: `${Math.min(barPct, 100)}%` }} />
                        </div>
                        <p className="text-[9px] text-center mt-0.5" style={{ color: sev.color }}>{a.stock_pct}%</p>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      {a.reorder_qty > 0 ? (
                        <span className="text-xs font-bold" style={{ color: '#24549c' }}>
                          {fmtFull(a.reorder_qty)} <span className="text-[9px] text-gray-400">{a.unit}</span>
                        </span>
                      ) : <span className="text-[10px] text-gray-300">-</span>}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {a.reorder_value > 0 ? (
                        <span className="text-xs font-semibold text-gray-700">{fmtINR(a.reorder_value)}</span>
                      ) : <span className="text-[10px] text-gray-300">-</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-500">
          <span>{filtered.length} product{filtered.length !== 1 ? 's' : ''} | Reorder value: INR {fmtFull(filtered.reduce((s, a) => s + a.reorder_value, 0))}</span>
          <span className="text-gray-400">Products with MIN/MAX stock thresholds</span>
        </div>
      </div>
    </div>
  );
};

export default StockReorderAlerts;
